<script setup lang="ts">
/**
 * 〈市集 › 發行者〉（`/plugin-publishers`；批153 批A）——admin 以上可看，**營運主管以上可改**。
 *
 * 左：發行者清單（第一方在最前；第一次進來時後端懶建第一方）。右：基本資料（顯示名稱、狀態、備註）
 * 與**簽章公鑰卡**——上傳的包，簽章公鑰必須是**該發行者**登錄的有效公鑰。信任一律比指紋
 * （sha256(raw 公鑰)），貼上公鑰的當下就在本機算出指紋，方便與發行者帶外核對。
 *
 * ★ 登錄公鑰是**網頁專屬**（API 金鑰 403）：金鑰外洩時不能替第一方偷掛一把攻擊者的公鑰。
 * ★ 公鑰全表唯一（含已撤銷）；撞到一律回同一句「這把公鑰無法登錄」，不透露屬於誰。
 *
 * 批B：〈開通發行者〉（營運主管以上）把一個客戶組織開通成第三方發行者——指定 key 前綴
 * （`^[a-z][a-z0-9]{2,13}_$`，前綴之間不可互相重疊、不可蓋到既有的第一方 key）與顯示名稱。
 * 開通之後，那個組織在客戶 portal 會多一個「開發者」區，自己登錄公鑰、登記 plugin、上傳版本；
 * 前綴、顯示名稱與停權仍由這一頁管（portal 端改不了）。
 */
import { computed, onMounted, ref, watch } from 'vue'

import UiButton from '@/components/UiButton.vue'
import UiModal from '@/components/UiModal.vue'
import * as pm from '@/api/pluginMarket'
import type { Publisher, PublisherDetail, PublisherKey } from '@/api/pluginMarket'
import { customerOptionLabel, isMarketCandidate } from '@/lib/customerLabel'
import { vEnter } from '@/lib/enterKey'
import {
  apiErrorText,
  canManageMarket,
  decodePublicKey,
  displayNameError,
  formatFingerprint,
  labelOf,
  PLUGIN_STATUS_LABELS,
  prefixError,
  publicKeyFingerprint,
  PUBLISHER_ERROR_TEXT,
  PUBLISHER_PREFIX_RE,
} from '@/lib/pluginMarket'
import { fmtSeen } from '@/lib/staleness'
import { useAuthStore } from '@/stores/auth'
import { useDataStore } from '@/stores/data'
import { useDialogsStore } from '@/stores/dialogs'
import { vCardTable } from '@/lib/cardTable'

const auth = useAuthStore()
const store = useDataStore()
const dialogs = useDialogsStore()
const isManager = computed(() => canManageMarket(auth.role))

const rows = ref<Publisher[]>([])
const loading = ref(false)
const loadError = ref('')
const selectedId = ref('')
const detail = ref<PublisherDetail | null>(null)

function errText(e: unknown, fallback: string): string {
  return apiErrorText(e, PUBLISHER_ERROR_TEXT, fallback)
}
async function fail(e: unknown, fallback: string) {
  await dialogs.notify({ title: '操作失敗', message: errText(e, fallback) })
}

async function load() {
  loading.value = true
  try {
    rows.value = (await pm.listPublishers()).items
    loadError.value = ''
    if (!rows.value.some((p) => p.id === selectedId.value)) selectedId.value = rows.value[0]?.id ?? ''
  } catch (e) {
    loadError.value = errText(e, '讀取發行者失敗')
  } finally {
    loading.value = false
  }
}
async function loadDetail() {
  const want = selectedId.value
  if (!want) {
    detail.value = null
    return
  }
  try {
    const d = await pm.getPublisher(want)
    // R3-7：快速切換時，慢回來的上一位發行者不可以蓋掉現在選的——不然公鑰會登錄到錯的發行者名下。
    if (d.id !== selectedId.value) return
    detail.value = d
    resetBasic()
  } catch (e) {
    if (want !== selectedId.value) return
    await fail(e, '讀取發行者失敗')
  }
}
watch(selectedId, () => {
  keyInput.value = ''
  keyLabel.value = ''
  void loadDetail()
})
onMounted(load)

// ---------------------------------------------------------------------------
// 基本資料
// ---------------------------------------------------------------------------
const basic = ref({ displayName: '', notes: '' })
function resetBasic() {
  basic.value = { displayName: detail.value?.display_name ?? '', notes: detail.value?.notes ?? '' }
}
const basicBusy = ref(false)
async function saveBasic() {
  const d = detail.value
  if (!d || !basic.value.displayName.trim()) return
  basicBusy.value = true
  try {
    await pm.updatePublisher(d.id, { display_name: basic.value.displayName.trim(), notes: basic.value.notes.trim() || null })
    await Promise.all([load(), loadDetail()])
  } catch (e) {
    await fail(e, '儲存失敗')
  } finally {
    basicBusy.value = false
  }
}
async function toggleStatus() {
  const d = detail.value
  if (!d) return
  const suspend = d.status !== 'suspended'
  const ok = await dialogs.confirm({
    title: suspend ? `停權「${d.display_name}」` : `恢復「${d.display_name}」`,
    message: suspend
      ? '停權後這個發行者底下的全部擴充從清單與市集消失、不再發下載票，也不能上傳新版本；已安裝的站台照常運作。確定要停權嗎？'
      : '恢復後底下的擴充重新可以列出、下載與上傳。確定要恢復嗎？',
    danger: suspend,
    confirmText: suspend ? '停權' : '恢復',
  })
  if (!ok) return
  try {
    await pm.updatePublisher(d.id, { status: suspend ? 'suspended' : 'active' })
    await Promise.all([load(), loadDetail()])
  } catch (e) {
    await fail(e, suspend ? '停權失敗' : '恢復失敗')
  }
}

// ---------------------------------------------------------------------------
// 簽章公鑰
// ---------------------------------------------------------------------------
const keyInput = ref('')
const keyLabel = ref('')
const keyFp = ref<string | null>(null)
const keyBusy = ref(false)
const keyShapeOk = computed(() => decodePublicKey(keyInput.value) !== null)
// 貼上就算指紋（sha256 在本機算，不送任何東西出去）。序號保護：慢算完的舊值不可蓋掉新值。
let fpSeq = 0
watch(keyInput, async (v) => {
  const my = ++fpSeq
  const fp = v.trim() ? await publicKeyFingerprint(v) : null
  if (my === fpSeq) keyFp.value = fp
})
async function addKey() {
  const d = detail.value
  if (!d || d.id !== selectedId.value || !keyShapeOk.value || keyBusy.value) return
  // R3-7：確認框顯示的指紋與實際送出的公鑰必須是**同一個值**——先把公鑰存成區域變數、當場重算指紋
  // （`keyFp` 是 watch 非同步算的，可能還是上一次貼上的值），確認後送的也是這個區域變數。
  const publicKey = keyInput.value.trim()
  const label = keyLabel.value.trim() || null
  const fp = await publicKeyFingerprint(publicKey)
  if (!fp) return
  const ok = await dialogs.confirm({
    title: '登錄簽章公鑰',
    message:
      `指紋：${formatFingerprint(fp)}\n\n` +
      `登錄之後，以這把鑰簽的包就能以「${d.display_name}」的名義上傳到市集、送到客戶的站台。` +
      '請先與發行者帶外核對這個指紋。確定要登錄嗎？',
    confirmText: '登錄',
  })
  if (!ok) return
  keyBusy.value = true
  try {
    await pm.addPublisherKey(d.id, publicKey, label)
    keyInput.value = ''
    keyLabel.value = ''
    await Promise.all([load(), loadDetail()])
  } catch (e) {
    await fail(e, '登錄公鑰失敗')
  } finally {
    keyBusy.value = false
  }
}
// ---------------------------------------------------------------------------
// 開通第三方發行者（批B #2，營運主管以上）
// ---------------------------------------------------------------------------
const createOpen = ref(false)
const createBusy = ref(false)
const createError = ref('')
const form = ref({ customerId: '', prefix: '', displayName: '', notes: '' })
/**
 * 已經是發行者的組織不再列出（一個組織最多一個發行者；後端同樣會擋）。
 * 平台（SaaS）客戶也不列：它們不開客戶 portal、沒有開發者區（後端 409 `platform_managed_customer`）。
 */
const publisherCustomerIds = computed(() => new Set(rows.value.map((p) => p.customer_id).filter(Boolean)))
const customerChoices = computed(() =>
  store.customers.filter((c) => isMarketCandidate(c) && !publisherCustomerIds.value.has(c.id)),
)
function openCreate() {
  form.value = { customerId: '', prefix: '', displayName: '', notes: '' }
  createError.value = ''
  createOpen.value = true
  void store.ensureCustomers().catch(() => {})
}
/** 選了組織而顯示名稱還空著時，先帶組織名（多數發行者就用自己的公司名）。 */
watch(
  () => form.value.customerId,
  (cid) => {
    if (form.value.displayName.trim()) return
    const c = store.customers.find((x) => x.id === cid)
    if (c) form.value.displayName = c.name.slice(0, 120)
  },
)
const prefixMsg = computed(() => prefixError(form.value.prefix))
const nameMsg = computed(() => (form.value.displayName ? displayNameError(form.value.displayName) : null))
const createInvalid = computed(() => {
  const f = form.value
  if (!f.customerId) return '請選擇客戶組織'
  if (!PUBLISHER_PREFIX_RE.test(f.prefix.trim())) return prefixMsg.value ?? '請輸入 key 前綴'
  return displayNameError(f.displayName)
})
async function doCreate() {
  if (createInvalid.value || createBusy.value) return
  const f = form.value
  createBusy.value = true
  createError.value = ''
  try {
    const p = await pm.createPublisher({
      customer_id: f.customerId,
      key_prefix: f.prefix.trim(),
      display_name: f.displayName.trim(),
      notes: f.notes.trim() || null,
    })
    createOpen.value = false
    await load()
    selectedId.value = p.id
  } catch (e) {
    createError.value = apiErrorText(e, PUBLISHER_ERROR_TEXT, '開通失敗')
  } finally {
    createBusy.value = false
  }
}

async function revokeKey(k: PublisherKey) {
  const d = detail.value
  if (!d) return
  const reason = await dialogs.prompt({
    title: '撤銷簽章公鑰',
    message:
      `指紋 ${formatFingerprint(k.fingerprint)}\n\n` +
      '撤銷後不能再用這把鑰上傳；客戶站台上這把鑰簽的版本從此不可安裝（已安裝的照常運作）。撤銷不可復原，同一把公鑰也不能再登錄。',
    label: '撤銷理由（選填，會記進稽核）',
    maxLength: 1000,
    danger: true,
    confirmText: '撤銷',
  })
  if (reason === null) return
  try {
    await pm.revokePublisherKey(d.id, k.id, reason)
    await Promise.all([load(), loadDetail()])
  } catch (e) {
    await fail(e, '撤銷公鑰失敗')
  }
}
</script>

<template>
  <div class="page">
    <div class="page-head">
      <h1>發行者</h1>
      <span class="count">{{ rows.length }}</span>
      <span class="spacer" />
      <UiButton v-if="isManager" variant="primary" @click="openCreate">＋ 開通發行者</UiButton>
    </div>
    <p class="subtle intro">
      能在市集發行擴充的組織。上傳的包，簽章公鑰必須是<strong>該發行者</strong>在這裡登錄的有效公鑰——任何人都能自產一把鑰，
      所以「誰的包」完全取決於這一頁登錄了什麼。
    </p>

    <div v-if="loading && !rows.length" class="loading-wrap"><span class="spinner lg" /></div>
    <p v-else-if="loadError" class="err">{{ loadError }}</p>

    <div v-else class="grid">
      <div class="card">
        <div class="card-head"><h3 class="section-title">發行者</h3></div>
        <ul class="list">
          <li v-for="p in rows" :key="p.id" :class="{ sel: selectedId === p.id }" @click="selectedId = p.id">
            <span class="pname">{{ p.display_name }}</span>
            <span v-if="p.first_party" class="chip">第一方</span>
            <code v-else class="mono">{{ p.key_prefix }}</code>
            <small class="cnt">
              有效公鑰 {{ p.active_keys ?? 0 }}・擴充 {{ p.plugins ?? 0 }}
              <span v-if="p.status === 'suspended'" class="sus-tag">已停權</span>
            </small>
          </li>
        </ul>
      </div>

      <div v-if="detail" class="right">
        <div class="card">
          <div class="card-head">
            <h3 class="section-title">基本資料</h3>
            <span class="spacer" />
            <span class="status-pill" :class="detail.status === 'active' ? 'active' : 'revoked'">
              {{ labelOf(PLUGIN_STATUS_LABELS, detail.status) }}
            </span>
          </div>
          <div class="form-grid two">
            <label class="field">
              <span class="field-label">顯示名稱（市集與發行者擔保上顯示）</span>
              <input v-model="basic.displayName" maxlength="120" :disabled="!isManager" />
            </label>
            <label class="field">
              <span class="field-label">{{ detail.first_party ? '類型' : '客戶組織／key 前綴' }}</span>
              <input
                :value="detail.first_party ? '第一方（我方）' : `${detail.customer_name ?? '—'}／${detail.key_prefix ?? '—'}`"
                disabled
              />
            </label>
          </div>
          <label class="field block">
            <span class="field-label">備註（內部）</span>
            <textarea v-model="basic.notes" rows="2" maxlength="4000" :disabled="!isManager" />
          </label>
          <div v-if="isManager" class="acts-row">
            <UiButton class="left-btn" variant="secondary" @click="toggleStatus">{{ detail.status === 'suspended' ? '恢復' : '停權…' }}</UiButton>
            <UiButton @click="resetBasic">還原</UiButton>
            <UiButton variant="primary" :loading="basicBusy" :disabled="!basic.displayName.trim()" @click="saveBasic">儲存</UiButton>
          </div>
          <p v-else class="subtle small">修改發行者需要營運主管以上的權限。</p>
        </div>

        <div class="card">
          <div class="card-head"><h3 class="section-title">簽章公鑰</h3></div>
          <div class="tbl-scroll" data-hscroll>
            <table v-card-table class="tbl card-table sticky-acts">
              <thead>
                <tr><th>指紋</th><th>名稱</th><th>狀態</th><th>登錄</th><th></th></tr>
              </thead>
              <tbody>
                <tr v-for="k in detail.keys" :key="k.id" :class="{ gone: k.status !== 'active' }">
                  <td class="fp-cell"><code class="mono fp" :title="k.public_key">{{ formatFingerprint(k.fingerprint) }}</code></td>
                  <td>{{ k.label ?? '—' }}</td>
                  <td class="nowrap">
                    <span class="status-pill" :class="k.status === 'active' ? 'active' : 'revoked'">
                      {{ k.status === 'active' ? '有效' : '已撤銷' }}
                    </span>
                    <div v-if="k.revoke_reason" class="subtle small">{{ k.revoke_reason }}</div>
                  </td>
                  <td class="nowrap subtle">{{ fmtSeen(k.created_at, { short: true }) }}・{{ k.created_by ?? '—' }}</td>
                  <td class="acts-cell">
                    <UiButton v-if="isManager && k.status === 'active'" size="sm" variant="danger" @click="revokeKey(k)">撤銷…</UiButton>
                  </td>
                </tr>
                <tr v-if="!detail.keys.length">
                  <td colspan="5" class="subtle empty-row">還沒有登錄任何公鑰——這個發行者目前上傳不了任何版本。</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="isManager" class="add-key">
            <div class="card-head sub"><h3 class="section-title">登錄新公鑰</h3></div>
            <div class="form-grid two">
              <label class="field">
                <span class="field-label">公鑰（raw 32 bytes 的標準 base64，44 字元）</span>
                <input v-model="keyInput" class="mono" placeholder="貼上發行者提供的 Ed25519 公鑰" maxlength="64" v-enter="addKey" />
              </label>
              <label class="field">
                <span class="field-label">名稱（選填）</span>
                <input v-model="keyLabel" maxlength="120" placeholder="例：2026 主鑰" v-enter="addKey" />
              </label>
            </div>
            <p class="fp-line">
              <template v-if="keyFp">指紋：<code class="mono fp">{{ formatFingerprint(keyFp) }}</code>（請與發行者帶外核對）</template>
              <span v-else-if="keyInput.trim()" class="err">公鑰要是 44 字元的標準 base64（raw 32 bytes）。</span>
              <span v-else class="subtle">貼上公鑰後，這裡會即時顯示它的指紋。</span>
            </p>
            <div class="acts-row">
              <UiButton variant="primary" :disabled="!keyShapeOk" :loading="keyBusy" @click="addKey">登錄公鑰…</UiButton>
            </div>
          </div>
          <p v-else class="subtle small">登錄與撤銷公鑰需要營運主管以上的權限。</p>
        </div>
      </div>
      <div v-else class="card">
        <div class="empty"><div class="empty-sub">請先在左側選一個發行者。</div></div>
      </div>
    </div>

    <!-- 開通第三方發行者 -->
    <UiModal :open="createOpen" title="開通發行者" width="600px" :close-on-backdrop="false" @close="createOpen = false">
      <p class="subtle">
        把一個客戶組織開通成第三方發行者。開通後那個組織在客戶 portal 會多一個「開發者」區，
        可以自己登錄簽章公鑰、登記擴充、上傳版本；前綴、顯示名稱與停權仍由這裡管理。
      </p>
      <p v-if="createError" class="err">{{ createError }}</p>
      <div class="form-grid">
        <label class="field">
          <span class="field-label">客戶組織</span>
          <select v-model="form.customerId">
            <option value="">（請選擇）</option>
            <option v-for="c in customerChoices" :key="c.id" :value="c.id">{{ customerOptionLabel(c) }}</option>
          </select>
          <small class="subtle">
            已經是發行者的組織不會出現在這裡（一個組織最多一個發行者）；合作平台（SaaS）的組織不使用客戶 portal、不能使用市集，也不會出現。
          </small>
        </label>
        <div class="form-grid two">
          <label class="field">
            <span class="field-label">key 前綴</span>
            <input
              v-model="form.prefix"
              class="mono"
              placeholder="例：acme_"
              maxlength="15"
              autocomplete="off"
              spellcheck="false"
              v-enter="doCreate"
            />
            <small v-if="prefixMsg" class="err">{{ prefixMsg }}</small>
            <small v-else class="subtle">小寫英文開頭、3–14 個小寫英數字，最後加一個底線。建立後不可更改。</small>
          </label>
          <label class="field">
            <span class="field-label">顯示名稱（市集上顯示）</span>
            <input v-model="form.displayName" maxlength="120" v-enter="doCreate" />
            <small v-if="nameMsg" class="err">{{ nameMsg }}</small>
            <small v-else class="subtle">1–120 字。</small>
          </label>
        </div>
        <label class="field">
          <span class="field-label">備註（內部，選填）</span>
          <textarea v-model="form.notes" rows="2" maxlength="4000" placeholder="例：合約編號、窗口" />
        </label>
      </div>
      <template #foot>
        <UiButton @click="createOpen = false">取消</UiButton>
        <UiButton variant="primary" :disabled="!!createInvalid" :loading="createBusy" @click="doCreate">開通</UiButton>
      </template>
    </UiModal>
  </div>
</template>

<style scoped>
.page-head h1 {
  margin: 0;
  font-size: var(--fs-20);
  color: var(--fg);
}
.count {
  font-size: var(--fs-12);
  color: var(--fg-3);
  background: var(--bg);
  padding: 1px 8px;
  border-radius: 999px;
}
.spacer {
  flex: 1;
}
.intro {
  margin: 0 0 var(--sp-3);
}
.err {
  margin: var(--sp-2) 0;
  color: var(--danger);
  font-size: var(--fs-12);
}
.grid {
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr);
  gap: var(--sp-4);
  align-items: start;
}
.grid > .card,
.right {
  min-width: 0;
}
.right {
  display: grid;
  /* 沒宣告欄＝隱含 auto 欄，會被卡片內表格的最小寬度撐開、窄螢幕整欄溢出畫面。 */
  grid-template-columns: minmax(0, 1fr);
  gap: var(--sp-4);
}
.right > .card {
  min-width: 0;
}
.right .card + .card {
  margin-top: 0;
}
.list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 2px;
}
.list li {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 2px var(--sp-2);
  padding: 8px 10px;
  border-radius: var(--r-sm);
  cursor: pointer;
  font-size: var(--fs-13);
  color: var(--fg-2);
}
.list li:hover {
  background: var(--bg-hover);
}
.list li.sel {
  background: var(--brand-100);
  color: var(--brand-700);
  font-weight: 600;
}
[data-theme='dark'] .list li.sel {
  background: var(--bg-active);
  color: var(--brand-400);
}
.pname {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.list .cnt {
  grid-column: 1 / -1;
  font-size: var(--fs-11);
  color: var(--fg-4);
  font-weight: 400;
}
.sus-tag {
  margin-left: 4px;
  color: var(--danger);
}
.mono {
  font-family: var(--font-mono);
}
.fp {
  font-size: var(--fs-12);
  /* 指紋是「8 碼一段」以空白分隔：只在段與段之間換行，不把一段拆開。 */
  word-break: normal;
  overflow-wrap: normal;
}
.fp-cell {
  min-width: 220px;
}
.small {
  font-size: var(--fs-11);
}
.nowrap {
  white-space: nowrap;
}
tr.gone td {
  color: var(--fg-4);
}
.empty-row {
  text-align: center;
  padding: var(--sp-5) 0;
}
.form-grid.two {
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}
.block {
  margin-top: var(--sp-3);
}
.card-head.sub {
  margin-top: var(--sp-4);
}
.acts-row {
  display: flex;
  justify-content: flex-end;
  gap: var(--sp-2);
  margin-top: var(--sp-3);
}
.acts-row .left-btn {
  margin-right: auto;
}
.fp-line {
  margin: var(--sp-2) 0 0;
  font-size: var(--fs-13);
}
@media (max-width: 900px) {
  .grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
